import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  type SelectChangeEvent
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import type { Company, Group, Role } from '@esg/esg-global-types';
import type { UserStatus } from '../../util/user_status';
import type { UsersTableFilters } from './hooks/useUsersTable';
import { GROUP_ADMIN_ROLE_FILTER, SUPER_ADMIN_ROLE_FILTER } from './user_filter_constants';
import { useSubscriptionContext } from '../../context/SubscriptionContext';
import type { ExceededQuotaKey } from '../../lib/app/subscription';

interface UsersTableToolbarProps {
  filters: UsersTableFilters;
  setFilter: <K extends keyof UsersTableFilters>(key: K, value: UsersTableFilters[K]) => void;
  groups: Array<Group>;
  roles: Array<Role>;
  handleAddSingle: () => void;
  handleImportCsv: () => void;
  /** When provided, renders an "Assign to company" action in the toolbar. */
  onAssignToCompany?: () => void;
}

const STATUS_OPTIONS: Array<{ value: UserStatus | 'all'; label: string }> = [
  { value: 'all', label: 'Any status' },
  { value: 'active', label: 'Active' },
  { value: 'invited', label: 'Invited' },
  { value: 'invite_expired', label: 'Invite expired' },
  { value: 'not_invited', label: 'Not invited' },
  { value: 'deleted', label: 'Deleted' }
];

const TYPE_OPTIONS: Array<{ value: UsersTableFilters['type']; label: string }> = [
  { value: 'all', label: 'Any type' },
  { value: 'internal', label: 'Internal' },
  { value: 'external', label: 'External' },
  { value: 'any', label: 'Combination' }
];

/** List-view toolbar: search, filters, and Add user / Bulk Import actions. */
export const UsersTableToolbar = ({
  filters,
  setFilter,
  groups,
  roles,
  handleAddSingle,
  handleImportCsv,
  onAssignToCompany
}: UsersTableToolbarProps) => {
  const { getExceededSubscriptionKeys } = useSubscriptionContext();
  const exceededSubscriptionKeys: Array<ExceededQuotaKey> = getExceededSubscriptionKeys([
    'internal_users'
  ]);
  const quotaExceeded = exceededSubscriptionKeys.length > 0;

  // Scope the company options to the selected group so the two filters stay
  // consistent; fall back to every company when no group is chosen.
  const companyOptions: Array<Company> =
    filters.group_id === 'all'
      ? groups.flatMap((g) => g.companies ?? [])
      : (groups.find((g) => g.id === filters.group_id)?.companies ?? []);

  const handleChangeGroup = (groupId: string): void => {
    setFilter('group_id', groupId);
    // Clear a now-orphaned company selection so the filter can't stick on a
    // company that is hidden by the new group scope.
    if (groupId !== 'all' && filters.company_id !== 'all') {
      const companyStillValid = (groups.find((g) => g.id === groupId)?.companies ?? []).some(
        (c) => c.id === filters.company_id
      );
      if (!companyStillValid) setFilter('company_id', 'all');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1,
        flexWrap: 'wrap'
      }}
    >
      <TextField
        size="small"
        placeholder="Search by name or email"
        value={filters.search}
        onChange={(e) => setFilter('search', e.target.value)}
        InputProps={{
          startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
        }}
        sx={{ minWidth: 240 }}
      />

      <FormControl size="small" sx={{ minWidth: 130 }}>
        <InputLabel>Role</InputLabel>
        <Select
          label="Role"
          value={filters.role}
          onChange={(e: SelectChangeEvent<string>) => setFilter('role', e.target.value)}
        >
          <MenuItem value="all">Any role</MenuItem>
          <MenuItem value={SUPER_ADMIN_ROLE_FILTER}>Super admin</MenuItem>
          <MenuItem value={GROUP_ADMIN_ROLE_FILTER}>Group admin</MenuItem>
          {roles.map((r) => (
            <MenuItem key={r.id} value={r.name.toLowerCase()}>
              {r.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Group</InputLabel>
        <Select
          label="Group"
          value={filters.group_id}
          onChange={(e: SelectChangeEvent<string>) => handleChangeGroup(e.target.value)}
        >
          <MenuItem value="all">Any group</MenuItem>
          {groups.map((g) => (
            <MenuItem key={g.id} value={g.id}>
              {g.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>Company</InputLabel>
        <Select
          label="Company"
          value={filters.company_id}
          onChange={(e: SelectChangeEvent<string>) => setFilter('company_id', e.target.value)}
        >
          <MenuItem value="all">Any company</MenuItem>
          {companyOptions.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Status</InputLabel>
        <Select
          label="Status"
          value={filters.status}
          onChange={(e: SelectChangeEvent<string>) =>
            setFilter('status', e.target.value as UsersTableFilters['status'])
          }
        >
          {STATUS_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 130 }}>
        <InputLabel>Type</InputLabel>
        <Select
          label="Type"
          value={filters.type}
          onChange={(e: SelectChangeEvent<string>) =>
            setFilter('type', e.target.value as UsersTableFilters['type'])
          }
        >
          {TYPE_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControlLabel
        sx={{ ml: 1 }}
        control={
          <Switch
            size="small"
            checked={filters.show_deleted}
            onChange={(e) => setFilter('show_deleted', e.target.checked)}
          />
        }
        label="Show deleted"
      />

      <Box sx={{ flex: 1 }} />

      <Button
        variant="contained"
        startIcon={<PersonAddIcon />}
        onClick={handleAddSingle}
        disabled={quotaExceeded}
      >
        Add user
      </Button>

      <Button
        variant="outlined"
        startIcon={<UploadFileIcon />}
        onClick={handleImportCsv}
        disabled={quotaExceeded}
      >
        Bulk Import
      </Button>

      {onAssignToCompany && (
        <Button variant="outlined" startIcon={<GroupAddIcon />} onClick={onAssignToCompany}>
          Assign to company
        </Button>
      )}
    </Box>
  );
};

export default UsersTableToolbar;
